import { observer } from 'mobx-react-lite';
import React, { useCallback, useMemo, useState } from 'react';
import { isMobile } from '@pascal/pascal-core';
import { kenoGameService } from '../../../../services/gameService';
import { kenoBetStore } from '../../../../store/kenoBetStore';
import useTranslation from '../../../../hooks/useTranslation';
import { TranslationKeys } from '../../../../core/constants/translationKeys';
import { parseNumberPrecisionByDoteCount } from '../../../../core/helpers/parseNumberByPrecision';
import { userInfoStore } from '../../../../store/userInfoStore';
import BetButton from '../../../../components/betButton';
import { BetStatus } from '../../../../core/constants/betStatus';
import { BetResponseCodes, MaxBetExceeded } from '../../../../core/constants/betResponseCodes';
import { ValidationType } from '../../../../core/constants/validationType';
import { applicationStore } from '../../../../store/applicationStore';
import { useBalance } from '../../../../hooks/useBalance';

export const BetButtonsBlock = observer(() => {
  const [isShowTicketAnimation, setIsShowTicketAnimation] = useState({
    bet: false,
    reBet: false,
    reBet2: false,
  });
  const { precision, limits, betAmount, userBalance } = userInfoStore;
  const { isDemo, demoBalance } = applicationStore;
  const {
    updateTickets,
    isBetsClosed,
    tickets,
    selectedNumbers,
    reBetModel,
    setTickets,
    kenoOdds,
    setNotification,
    isBetButtonActive,
  } = kenoBetStore;
  const t = useTranslation();

  const balance = useBalance(isDemo, userBalance, demoBalance);

  const handleIsShowTicketAnimation = useCallback((type) => {
    setIsShowTicketAnimation((prev) => {
      return {
        ...prev,
        [type]: true,
      };
    });
    setTimeout(() => {
      setIsShowTicketAnimation((prev) => {
        return {
          ...prev,
          [type]: false,
        };
      });
    }, 360);
  }, []);

  const handleDoBet = useCallback(() => {
    updateTickets(true);
    kenoGameService.doSpin();
    if (isMobile()) {
      document.querySelector('.keno-game-container').scrollTop = 0;
    }
    if (document.querySelector('.betslip-ticketsitem-list-wrapper')) {
      document.querySelector('.betslip-ticketsitem-list-wrapper').scrollTop = 0;
    }
    if (!tickets.length) {
      handleIsShowTicketAnimation('bet');
    }
  }, [updateTickets, tickets, handleIsShowTicketAnimation]);

  const isBetActive = useMemo(() => {
    return (
      !isBetsClosed &&
      (tickets.length || selectedNumbers.length) &&
      +betAmount <= limits.maxBet &&
      +betAmount >= limits.minBet
    );
  }, [isBetsClosed, tickets, selectedNumbers, betAmount, limits]);

  const doubleReBetIsInactive = useMemo(() => {
    return reBetModel?.some((el) => el.amount * 2 > limits.maxBet);
  }, [reBetModel, limits]);

  const handleReBet = useCallback(
    (isDouble) => {
      const ticketsAmount = tickets
        .filter((el) => el.status === BetStatus.Pending)
        .reduce((acc, current) => acc + current.amount, 0);
      if (ticketsAmount > balance) {
        setNotification(BetResponseCodes[ValidationType.NotEnoughMoney]);
        return;
      }
      if (doubleReBetIsInactive && isDouble) {
        setNotification(MaxBetExceeded);
        return;
      }
      const betModel = reBetModel.map(({ amount, ticketNumber, numbers }) => {
        return {
          amount: parseNumberPrecisionByDoteCount(amount, isDouble ? 2 : 1, precision),
          ticketNumber,
          numbers,
          status: BetStatus.Pending,
          possibleWin: parseNumberPrecisionByDoteCount(
            kenoOdds[numbers.length][numbers.length],
            amount * (isDouble ? 2 : 1),
            precision
          ),
        };
      });
      handleIsShowTicketAnimation(isDouble ? 'reBet2' : 'reBet');
      setTickets(betModel);
      if (isMobile()) {
        document.querySelector('.keno-game-container').scrollTop = 0; // For modern browsers
      }
      if (document.querySelector('.betslip-ticketsitem-list-wrapper')) {
        document.querySelector('.betslip-ticketsitem-list-wrapper').scrollTop = 0;
      }
      kenoGameService.doSpin();
    },
    [
      reBetModel,
      kenoOdds,
      precision,
      setTickets,
      handleIsShowTicketAnimation,
      balance,
      doubleReBetIsInactive,
      setNotification,
      tickets,
    ]
  );

  return (
    <div className='keno-bet-btn-wrapper'>
      {!reBetModel ? (
        <div className='ken-bet-btn-block'>
          <div
            className={`ticket-move-block move-bet-ticket ${
              isShowTicketAnimation.bet ? 'active' : ''
            }`}
          />
          <BetButton
            text={t(TranslationKeys.BetButton)}
            className='bet-btn'
            isDisabled={!isBetActive}
            handleClick={handleDoBet}
          />
        </div>
      ) : null}
      {reBetModel ? (
        <>
          <div className='ken-bet-btn-block'>
            <div
              className={`ticket-move-block move-rebet-ticket ${
                isShowTicketAnimation.reBet ? 'active' : ''
              }`}
            />
            <BetButton
              text={t(TranslationKeys.ReBet)}
              className='rebet-btn'
              isDisabled={!isBetButtonActive}
              handleClick={() => handleReBet(false)}
            />
          </div>
          <div className='ken-bet-btn-block'>
            <div
              className={`ticket-move-block move-rebet-x2-ticket ${
                isShowTicketAnimation.reBet2 ? 'active' : ''
              }`}
            />
            <BetButton
              text={t(TranslationKeys.ReBet)}
              className='rebet-btn-2x'
              isDisabled={!isBetButtonActive}
              handleClick={() => handleReBet(true)}
            >
              <span className='bet-btn-txt'>x2</span>
            </BetButton>
          </div>
        </>
      ) : null}
    </div>
  );
});
